type Reveal = number[];
type MarkerKeyframe = [number, number, number, number, number];

type Stroke = {
  element: SVGPathElement;
  length: number;
  keyframes: Reveal[];
};

type Marker = {
  element: SVGPathElement;
  keyframes: MarkerKeyframe[];
};

// The source exports are 250 frames at 50 fps and play once, holding their artwork.
const frameRate = 50;
const duration = 5000;
const lastMotionFrame = 110;

class FeatureShowcaseAnimation extends HTMLElement {
  private strokes: Stroke[] = [];

  private markers: Marker[] = [];

  private observer: IntersectionObserver | null = null;

  private motion: MediaQueryList | null = null;

  private animation = 0;

  private initialization = 0;

  private elapsed = 0;

  private previousTime: number | null = null;

  private previousFrame = -1;

  private visible = false;

  connectedCallback() {
    // ACF previews and Storybook insert these elements dynamically, after DOMContentLoaded.
    this.initialization = requestAnimationFrame(() => this.initialize());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.initialization);
    this.stop();
    this.visible = false;
    this.observer?.disconnect();
    this.motion?.removeEventListener('change', this.syncPlayback);
    this.ownerDocument.removeEventListener('visibilitychange', this.syncPlayback);
  }

  private initialize() {
    this.strokes = Array.from(this.querySelectorAll<SVGPathElement>('[data-reveal]')).map((element) => ({
      element,
      length: Number(element.dataset.length),
      keyframes: JSON.parse(element.dataset.reveal ?? '[]') as Reveal[],
    }));
    this.markers = Array.from(this.querySelectorAll<SVGPathElement>('[data-marker]')).map((element) => ({
      element,
      keyframes: JSON.parse(element.dataset.marker ?? '[]') as MarkerKeyframe[],
    }));
    if (!this.strokes.length || !this.markers.length) return;

    this.motion = this.ownerDocument.defaultView?.matchMedia('(prefers-reduced-motion: reduce)') ?? null;
    this.motion?.addEventListener('change', this.syncPlayback);
    this.ownerDocument.addEventListener('visibilitychange', this.syncPlayback);
    this.renderFrame(Math.floor(this.elapsed * frameRate / 1000));
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.intersectionRatio >= 0.3;
      this.syncPlayback();
    }, { threshold: [0, 0.3] });
    this.observer.observe(this);
    this.syncPlayback();
  }

  private syncPlayback = () => {
    if (this.motion?.matches) {
      this.elapsed = duration;
      this.renderFrame(duration * frameRate / 1000);
    }
    if (!this.visible || this.ownerDocument.hidden || !this.isConnected || this.elapsed >= duration) {
      this.stop();
    } else if (!this.animation) {
      this.animation = requestAnimationFrame(this.tick);
    }
  };

  private stop() {
    cancelAnimationFrame(this.animation);
    this.animation = 0;
    this.previousTime = null;
  }

  private tick = (timestamp: number) => {
    if (this.previousTime !== null) this.elapsed = Math.min(duration, this.elapsed + timestamp - this.previousTime);
    this.previousTime = timestamp;
    const frame = Math.min(lastMotionFrame, Math.floor(this.elapsed * frameRate / 1000));
    if (frame !== this.previousFrame) {
      this.renderFrame(frame);
      this.previousFrame = frame;
    }
    if (this.elapsed >= duration) this.stop();
    else this.animation = requestAnimationFrame(this.tick);
  };

  private renderFrame(frame: number) {
    this.strokes.forEach(({ element, length, keyframes }) => {
      // Each interval is a growing section of one vector stroke, in SVG units.
      const keyframe = keyframes.findLast((value) => value[0] <= frame);
      if (!keyframe || keyframe.length === 1) {
        element.style.visibility = 'hidden';
        return;
      }
      element.style.visibility = '';
      const dashes = [0];
      let previousEnd = 0;
      for (let i = 1; i < keyframe.length; i += 2) {
        dashes.push(keyframe[i] - previousEnd, keyframe[i + 1] - keyframe[i]);
        previousEnd = keyframe[i + 1];
      }
      dashes.push(length + 1);
      element.setAttribute('stroke-dasharray', dashes.join(' '));
    });

    this.markers.forEach(({ element, keyframes }, markerIndex) => {
      // The opening is one diamond. Avoid compositing eight antialiased edges in the same place.
      element.style.visibility = frame < 34 && markerIndex > 0 ? 'hidden' : '';
      const nextIndex = keyframes.findIndex((value) => value[0] > frame);
      const start = keyframes[nextIndex < 0 ? keyframes.length - 1 : Math.max(0, nextIndex - 1)];
      const end = nextIndex < 0 ? start : keyframes[nextIndex];
      const progress = start === end ? 0 : (frame - start[0]) / (end[0] - start[0]);
      const interpolate = (index: number) => start[index] + (end[index] - start[index]) * progress;
      element.setAttribute('transform', `translate(${interpolate(1)} ${interpolate(2)}) scale(${interpolate(3)})`);
      element.style.fill = `color-mix(in srgb, var(--showcase-accent) ${interpolate(4) * 100}%, black)`;
    });
  }
}

if (!customElements.get('skill-showcase-animation')) {
  customElements.define('skill-showcase-animation', FeatureShowcaseAnimation);
}
